import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';
import NewCampaignUX from './NewCampaignUX';

const EditCampaign = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [campaignData, setCampaignData] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  useEffect(() => {
    const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

    const fetchCampaign = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`/api/vehicle-campaigns/${campaignId}`, {
          headers: authHeader()
        });

        // Transform the data to match the expected format for NewCampaignUX
        const transformedData = {
          selectedLocations: data.selectedLocations || [],
          campaignType: data.campaignType || null,
          selectedVehicles: data.selectedVehicles || [],
          dateRange: data.dateRange || null,
          stopDuration: data.stopDuration || { min: 15, max: 720 },
          targetingRadius: data.targetingRadius || 0.5,
          selectedStops: data.selectedStops || [],
          breadcrumbSpacing: data.breadcrumbSpacing || null,
          selectedRoutes: data.selectedRoutes || [],
          proximityRadius: data.proximityRadius || null,
          proximityDuration: data.proximityDuration || null,
          campaignChannel: data.campaignChannel || null,
          audienceMethod: data.audienceMethod || null,
          targetSegments: data.targetSegments || [],
          customAudience: data.customAudience || null,
          smsTemplate: data.smsTemplate || null,
          emailTemplate: data.emailTemplate || null,
          adPlatform: data.adPlatform || null,
          googleAdsConfig: data.googleAdsConfig || null,
          // Meta Ads specific data
          metaAds: {
            campaignObjective: data.metaAds?.campaignObjective || null,
            campaignName: data.metaAds?.campaignName || '',
            buyingType: data.metaAds?.buyingType || 'AUCTION',
            abTestEnabled: data.metaAds?.abTestEnabled || false,
            advantagePlusBudget: data.metaAds?.advantagePlusBudget || false,
            abTestVariable: data.metaAds?.abTestVariable || 'CREATIVE',
            abTestDuration: data.metaAds?.abTestDuration || '7',
            abTestWinnerBy: data.metaAds?.abTestWinnerBy || 'COST_PER_RESULT',
            specialAdCategory: data.metaAds?.specialAdCategory || 'NONE',
            budgetType: data.metaAds?.budgetType || 'DAILY',
            budgetAmount: data.metaAds?.budgetAmount || '',
            currency: data.metaAds?.currency || 'USD',
            bidStrategy: data.metaAds?.bidStrategy || 'HIGHEST_VOLUME',
            startDate: data.metaAds?.startDate || null,
            endDate: data.metaAds?.endDate || null,
            budgetSchedulingEnabled: data.metaAds?.budgetSchedulingEnabled || false,
            budgetTimeSlots: data.metaAds?.budgetTimeSlots || [],
            adSetName: data.metaAds?.adSetName || '',
            adSetBudgetType: data.metaAds?.adSetBudgetType || 'DAILY',
            adSetBudgetAmount: data.metaAds?.adSetBudgetAmount || '',
            adSetCurrency: data.metaAds?.adSetCurrency || 'USD',
            adSetBidStrategy: data.metaAds?.adSetBidStrategy || 'HIGHEST_VOLUME',
            adSetStartDate: data.metaAds?.adSetStartDate || null,
            adSetEndDate: data.metaAds?.adSetEndDate || null,
            adSetBudgetSchedulingEnabled: data.metaAds?.adSetBudgetSchedulingEnabled || false,
            adSetBudgetTimeSlots: data.metaAds?.adSetBudgetTimeSlots || [],
            advantageDetailedTargeting: data.metaAds?.advantageDetailedTargeting || false,
            customTargetingOptions: data.metaAds?.customTargetingOptions || [],
            placements: data.metaAds?.placements || [],
            adFormat: data.metaAds?.adFormat || 'single',
            mediaFiles: data.metaAds?.mediaFiles || [],
            carouselCards: data.metaAds?.carouselCards || [],
            collectionCover: data.metaAds?.collectionCover || null,
            selectedCatalog: data.metaAds?.selectedCatalog || '',
            showBestPerformingFirst: data.metaAds?.showBestPerformingFirst || false,
            cropSettings: data.metaAds?.cropSettings || {},
            customCatalogs: data.metaAds?.customCatalogs || [],
            primaryText: data.metaAds?.primaryText || '',
            headline: data.metaAds?.headline || '',
            description: data.metaAds?.description || '',
            displayLink: data.metaAds?.displayLink || '',
            callToAction: data.metaAds?.callToAction || 'LEARN_MORE',
          }
        };

        setCampaignData(transformedData);
      } catch (error) {
        console.error('Failed to fetch campaign:', error);
        message.error('Failed to load campaign data. Please try again.');
        navigate('/client/campaigns');
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId, token, navigate]);

  const handleUpdate = (updatedData) => {
    setCampaignData(updatedData);
  };

  const handleSave = async (finalData) => {
    try {
      // Transform the data back to match the API format
      const apiData = {
        ...finalData,
        id: campaignId,
        // Remove any UI-specific fields and transform as needed
      };

      await axios.put(`/api/vehicle-campaigns/${campaignId}`, apiData, {
        headers: authHeader()
      });

      message.success('Campaign updated successfully!');
      navigate('/client/campaigns');
    } catch (error) {
      console.error('Failed to update campaign:', error);
      message.error('Failed to update campaign. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!campaignData) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px'
      }}>
        <h2>Campaign not found</h2>
        <p>The campaign you're trying to edit doesn't exist or you don't have permission to view it.</p>
      </div>
    );
  }

  return (
    <NewCampaignUX
      campaignData={campaignData}
      onUpdate={handleUpdate}
      onSave={handleSave}
      isEditing={true}
      campaignId={campaignId}
    />
  );
};

export default EditCampaign;
